import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Comment } from '../../../shared/api/types'
import { authorDisplayName } from '../../../shared/utils/formatAuthor'
import { confirm } from '../../../shared/ui/confirmStore'
import { MentionText } from './MentionText'
import { CommentImages } from './commentImages'

// Inline SVG-иконки в стиле проекта (lucide-подобный stroke, 24x24,
// currentColor) — как в Layout.tsx. 14px чтобы влезали в action-bar
// над компактной карточкой коммента, не растягивая её.
const ICON_STROKE = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none' as const }
const ICON_PROPS = {
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const IconReply = () => (
  <svg {...ICON_STROKE} {...ICON_PROPS}>
    <polyline points="9 17 4 12 9 7" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </svg>
)

const IconEdit = () => (
  <svg {...ICON_STROKE} {...ICON_PROPS}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const IconTrash = () => (
  <svg {...ICON_STROKE} {...ICON_PROPS}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

type Props = {
  comment: Comment
  /** id текущего пользователя — нужно, чтобы открыть редактирование только на своих комментах. */
  currentUserId: string | undefined
  /** Подсветить (для focus-deep-link или клика по цитате родителя). */
  isFocused?: boolean
  /** Если задан — на цитате родителя ставится клик, который пытается прокрутить к родителю. */
  onParentClick?: (parentId: string) => void
  /** Колбэк нажатия «Ответить» — родитель открывает плашку над инпутом. */
  onReply?: (comment: Comment) => void
  /** Колбэк сохранения inline-edit (только для своих). Возвращает true если успешно. */
  onSubmitEdit?: (id: string, text: string) => Promise<void>
  /** Колбэк удаления собственного коммента (показывается кнопка-мусорка с confirm). */
  onDelete?: (id: string) => Promise<void>
  /** Опционально: бейдж/декор сверху коммента (например, «К квоте: ...»). */
  topBadge?: React.ReactNode
  /** Дополнительный CSS-класс контейнера (для левой границы у комментов с quoteId). */
  containerClassName?: string
}

/**
 * Карточка одного комментария: автор + дата + текст с @-меншенами.
 *
 * Поддерживает:
 *  - двойной клик на собственном комменте → inline-edit textarea с
 *    кнопками «Сохранить» (Enter) / «Отмена» (Esc);
 *  - hover-кнопку «Ответить» (видна на любом комменте, если есть onReply);
 *  - цитату родителя сверху (если c.parent есть) — кликабельную, ведёт на родителя.
 */
export function CommentItem({
  comment,
  currentUserId,
  isFocused = false,
  onParentClick,
  onReply,
  onSubmitEdit,
  onDelete,
  topBadge,
  containerClassName = '',
}: Props) {
  const isOwn = !!currentUserId && comment.userId === currentUserId
  const canEdit = isOwn && !!onSubmitEdit
  const canDelete = isOwn && !!onDelete

  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!onDelete) return
    if (
      !(await confirm({
        title: 'Удалить этот комментарий?',
        message: 'Действие необратимо.',
        danger: true,
        confirmText: 'Удалить',
      }))
    )
      return
    setDeleting(true)
    try {
      await onDelete(comment.id)
    } finally {
      setDeleting(false)
    }
  }

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.text)
  const [saving, setSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // При входе в режим редактирования — фокус + автогрowth.
  useLayoutEffect(() => {
    if (!editing) return
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const max = 6 * 20 + 12
    el.style.height = Math.min(el.scrollHeight, max) + 'px'
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden'
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }, [editing])

  // Если коммент пришёл свежий по WS — синхронизируем черновик с актуальным текстом.
  useEffect(() => {
    if (!editing) setDraft(comment.text)
  }, [comment.text, editing])

  const startEdit = () => {
    if (!canEdit) return
    setDraft(comment.text)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setDraft(comment.text)
  }

  const submitEdit = async () => {
    if (!onSubmitEdit) return
    const next = draft.trim()
    // Пустой текст можно оставить, только если в комментарии есть картинка.
    const hasImages = (comment.images?.length ?? 0) > 0
    if ((!next && !hasImages) || next === comment.text) {
      cancelEdit()
      return
    }
    setSaving(true)
    try {
      await onSubmitEdit(comment.id, next)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
      return
    }
    if (e.key === 'Enter' && !e.altKey && !e.shiftKey) {
      e.preventDefault()
      void submitEdit()
    }
  }

  const authorName = comment.user ? authorDisplayName(comment.user) : '—'
  const parentAuthor = comment.parent?.user ? authorDisplayName(comment.parent.user) : '—'

  return (
    <div
      data-focus-id={comment.id}
      onDoubleClick={canEdit && !editing ? startEdit : undefined}
      className={`group relative rounded-lg px-3 py-2 transition-shadow ${
        isFocused ? 'bg-brand-50' : 'bg-gray-50'
      } ${containerClassName}`}
    >
      {topBadge}

      {comment.parent && (
        <button
          type="button"
          onClick={
            comment.parent.id && onParentClick ? () => onParentClick(comment.parent!.id) : undefined
          }
          className={`mb-1 block w-full rounded border-l-2 border-gray-300 bg-white/60 px-2 py-1 text-left text-[11px] text-gray-500 hover:bg-white ${
            onParentClick ? 'cursor-pointer' : 'cursor-default'
          }`}
          title={comment.parent.text}
        >
          <span className="font-medium text-gray-600">↳ {parentAuthor}:</span>{' '}
          <span className="text-gray-500">
            {!comment.parent.text
              ? 'Картинка'
              : comment.parent.text.length > 80
                ? comment.parent.text.slice(0, 80) + '…'
                : comment.parent.text}
          </span>
        </button>
      )}

      <div className="mb-1 leading-tight">
        <div className="text-xs font-medium text-gray-700">
          {authorName} ·{' '}
          <span className="font-normal text-gray-400">
            {new Date(comment.createdAt).toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {comment.editedAt && <span className="ml-1 font-normal text-gray-400">· изменено</span>}
        </div>
        {comment.user?.email && (
          <div className="text-[11px] text-gray-400">{comment.user.email}</div>
        )}
      </div>

      {editing ? (
        <div className="space-y-1">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={saving}
            rows={1}
            className="w-full resize-none rounded border border-gray-300 px-2 py-1 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex items-center justify-end gap-2 text-[11px]">
            <span className="mr-auto text-gray-400">Enter — сохранить, Esc — отмена</span>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="rounded px-2 py-0.5 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => void submitEdit()}
              disabled={
                saving ||
                (!draft.trim() && !comment.images?.length) ||
                draft.trim() === comment.text
              }
              className="rounded bg-brand-600 px-2 py-0.5 font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Сохранить
            </button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          <MentionText text={comment.text} />
        </div>
      )}
      <CommentImages images={comment.images} />

      {/* Hover-кнопки: «Ответить» всем, «Изменить»/«Удалить» только своим. */}
      {!editing && (
        <div className="absolute right-2 top-1.5 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {onReply && (
            <button
              type="button"
              onClick={() => onReply(comment)}
              title="Ответить на комментарий"
              className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            >
              <IconReply />
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={startEdit}
              title="Редактировать (двойной клик)"
              className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            >
              <IconEdit />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              title="Удалить комментарий"
              className="rounded p-1 text-gray-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
            >
              <IconTrash />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
